import { AsyncPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppleDeviceProfileResolverActions, DevicesRouterStoreSelectors, selectProfileByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import {
  AsyncResultComponent,
  PaneComponent,
  PanelComponent,
  PanelFooterComponent,
  YesNoIconComponent,
} from '@rocksoft/cdk';
import { DeviceInformation } from '@rocksoft/mdm-sdk-v2';
import { isNotNullOrUndefined, isNotUndefined } from '@rocksoft/shared';
import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { OSSupportedPipe } from '../../../pipes/os-supported.pipe';
import { AppleCommandProgressService } from '../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../utilities/command-spinner/command-spinner.component';
import { QueryList } from '../../../viewmodels/apple/commands/device-information.command';
import { CommandProgress } from '../../../viewmodels/apple/signalr/command-context.model';
@Component({
  selector: 'ml-mdm-apple-device-information',
  templateUrl: './apple-device-information.component.html',
  styleUrls: ['./apple-device-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PanelComponent,
    AsyncResultComponent,
    PaneComponent,
    YesNoIconComponent,
    PanelFooterComponent,
    CommandSpinnerComponent,
    FaIconComponent,
    AsyncPipe,
    DecimalPipe,
    PercentPipe,
    OSSupportedPipe,
  ],
})
export class AppleDeviceInformationComponent {
  private readonly mdmService = inject(AppleMdmService);
  private readonly store = inject(Store);
  private readonly commandProgressStore = inject(AppleCommandProgressService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly udid = signal('');

  public readonly udid$: Observable<string> = toObservable(this.udid).pipe(
    filter(isNotNullOrUndefined),
    distinctUntilChanged()
  );

  public readonly apple$: Observable<DeviceInformation> = this.udid$.pipe(
    switchMap(() => this.store.select(selectProfileByType('device-information')).pipe(filter(isNotUndefined)))
  );

  queryList: QueryList = new QueryList();

  private _commandProgress = new Subject<CommandProgress>();
  public readonly commandProgress$: Observable<CommandProgress> = this.udid$.pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressStore.commandsOfRequestType(_udid, 'DeviceInformation'))
    )
  );
  constructor() {
    effect((onCleanup) => {
      const sub = this.store
        .select(DevicesRouterStoreSelectors.selectUdid)
        .pipe(filter(isNotNullOrUndefined), distinctUntilChanged())
        .subscribe((_udid) => {
          this.udid.set(_udid);
        });
      onCleanup(() => {
        sub.unsubscribe();
      });
    });

    effect((onCleanup) => {
      const _udid = this.udid();
      if (!_udid) return;
      const sub = this.commandProgress$
        .pipe(
          filter((progress) => progress.status === 'Acknowledged'),
          distinctUntilChanged()
        )
        .subscribe((progress) => {
          this.store.dispatch(
            AppleDeviceProfileResolverActions.profileRequested({
              udid: progress.udid,
              profileType: 'device-information',
            })
          );
        });
      onCleanup(() => {
        sub.unsubscribe();
      });
    });
  }

  refresh() {
    const _udid = this.udid();
    if (!_udid) return;
    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'DeviceInformation',
    };
    this._commandProgress.next(command);
    this.mdmService.getDeviceInformation(_udid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }
}
